import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Clock,
  Star,
  XCircle,
  Download,
  Search,
  LogOut,
  FileText,
  MoreVertical,
  Eye,
  Plus,
  Trash2,
  Edit,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pending", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  reviewed: { label: "Reviewed", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  shortlisted: { label: "Shortlisted", color: "bg-[#BEF264]/10 text-[#BEF264] border-[#BEF264]/20" },
  rejected: { label: "Rejected", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="border border-zinc-900 bg-black/40 p-6 group hover:border-zinc-800 transition-colors duration-300">
    <div className="flex items-center justify-between mb-4">
      <div className={`w-8 h-8 flex items-center justify-center border ${accent || "border-zinc-800"}`}>
        <Icon className="w-4 h-4 text-zinc-500" />
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-700">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white tracking-tight font-['Space_Grotesk']">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0, shortlisted: 0, rejected: 0 });
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ title: "", description: "", details: "", status: "open" });
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [appsRes, statsRes, rolesRes] = await Promise.all([
        axios.get(`${API}/applications`, { params: filterRole !== "all" ? { role: filterRole } : {} }),
        axios.get(`${API}/applications/stats`, { params: filterRole !== "all" ? { role: filterRole } : {} }),
        axios.get(`${API}/roles`),
      ]);
      setApps(appsRes.data);
      setStats(statsRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [filterRole]);

  useEffect(() => {
    const isAdmin = sessionStorage.getItem("bs_admin");
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const updateStatus = async (appId, status) => {
    try {
      const response = await axios.patch(`${API}/applications/${appId}/status`, { status });
      const rejectionEmail = response.data?.rejection_email;

      if (status === "rejected" && rejectionEmail?.attempted) {
        if (rejectionEmail.sent) {
          toast.success("Status updated to rejected and rejection email sent");
        } else {
          toast.warning("Status updated to rejected, but rejection email could not be sent");
        }
      } else {
        toast.success(`Status updated to ${status}`);
      }
      fetchData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const downloadResume = (appId, filename) => {
    const link = document.createElement("a");
    link.href = `${API}/applications/${appId}/resume`;
    link.download = filename;
    link.click();
  };

  const handleLogout = () => {
    sessionStorage.removeItem("bs_admin");
    navigate("/admin");
  };

  const filtered = apps.filter((a) => {
    const matchSearch =
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase()) ||
      (a.github_url || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.tech_interest || "").toLowerCase().includes(search.toLowerCase()) ||
      (a.role_title || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ title: role.title, description: role.description, details: role.details, status: role.status });
    } else {
      setEditingRole(null);
      setRoleForm({ title: "", description: "", details: "", status: "open" });
    }
    setRoleDialogOpen(true);
  };

  const handleRoleSubmit = async () => {
    if (!roleForm.title || !roleForm.description || !roleForm.details) {
      toast.error("All fields are required");
      return;
    }
    try {
      if (editingRole) {
        await axios.patch(`${API}/roles/${editingRole.id}`, roleForm);
        toast.success("Role updated successfully");
      } else {
        await axios.post(`${API}/roles`, roleForm);
        toast.success("Role created successfully");
      }
      setRoleDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(editingRole ? "Failed to update role" : "Failed to create role");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm("Are you sure you want to delete this role? This action cannot be undone.")) return;
    try {
      await axios.delete(`${API}/roles/${roleId}`);
      toast.success("Role deleted successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete role");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bstext.png"
              alt="Blanksage"
              className="h-5 sm:h-6 brightness-0 invert"
              data-testid="dashboard-logo"
            />
            <div className="h-4 w-px bg-zinc-800" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Recruitment
            </span>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="h-9 px-4 bg-transparent border border-zinc-800 text-zinc-500 hover:bg-white hover:text-black font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-10">
        {/* Roles Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Briefcase className="w-4 h-4 text-zinc-500" />
            <h2 className="text-white text-sm font-semibold tracking-tight">Job Roles</h2>
          </div>
          <Button
            onClick={() => openRoleDialog()}
            className="h-9 px-4 bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300"
          >
            <Plus className="w-3.5 h-3.5 mr-2" />
            Add Role
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
          {roles.map((role) => (
            <div
              key={role.id}
              className="border border-zinc-900 bg-black/40 p-4 group hover:border-zinc-800 transition-colors duration-300"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-white text-sm font-medium">{role.title}</h3>
                <Badge
                  className={`rounded-full text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                    role.status === "open"
                      ? "bg-[#BEF264]/10 text-[#BEF264] border-[#BEF264]/20"
                      : "bg-zinc-800/50 text-zinc-500 border-zinc-800"
                  }`}
                >
                  {role.status}
                </Badge>
              </div>
              <p className="text-zinc-600 text-xs line-clamp-2 mb-3">{role.description}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => openRoleDialog(role)}
                  className="flex-1 h-7 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700 transition-colors duration-200 flex items-center justify-center"
                >
                  <Edit className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleDeleteRole(role.id)}
                  className="flex-1 h-7 border border-zinc-800 text-zinc-500 hover:text-red-400 hover:border-red-500/30 transition-colors duration-200 flex items-center justify-center"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          {roles.length === 0 && !loading && (
            <div className="col-span-full text-center py-8 border border-zinc-900 bg-black/40">
              <p className="text-zinc-600 text-xs font-mono">No roles created yet</p>
            </div>
          )}
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-10"
          data-testid="stats-grid"
        >
          <StatCard icon={Users} label="Total" value={stats.total} />
          <StatCard icon={Clock} label="Pending" value={stats.pending} accent="border-amber-500/20" />
          <StatCard icon={Eye} label="Reviewed" value={stats.reviewed} accent="border-blue-500/20" />
          <StatCard icon={Star} label="Shortlisted" value={stats.shortlisted} accent="border-[#BEF264]/20" />
          <StatCard icon={XCircle} label="Rejected" value={stats.rejected} accent="border-red-500/20" />
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-6"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, technology, or role..."
              className="h-11 bg-black/40 border border-zinc-900 rounded-none pl-10 text-sm text-white placeholder:text-zinc-700 focus:border-zinc-700 focus:ring-0 transition-colors duration-300"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {["all", "pending", "reviewed", "shortlisted", "rejected"].map((s) => (
              <button
                key={s}
                data-testid={`filter-${s}`}
                onClick={() => setFilterStatus(s)}
                className={`h-11 px-5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors duration-300 whitespace-nowrap ${
                  filterStatus === s
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-zinc-600 border-zinc-900 hover:border-zinc-700 hover:text-zinc-400"
                }`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="h-11 px-4 bg-black/40 border border-zinc-900 text-zinc-400 font-mono text-[10px] uppercase tracking-[0.15em] focus:border-zinc-700 focus:ring-0 transition-colors duration-300"
            data-testid="filter-role"
          >
            <option value="all">All Roles</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="border border-zinc-900 bg-black/40"
          data-testid="applications-table-container"
        >
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-zinc-800 border-t-white rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="no-applications">
              <FileText className="w-8 h-8 text-zinc-800 mx-auto mb-4" />
              <p className="text-zinc-600 text-sm">No applications found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-900 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12 pl-6">
                    Applicant
                  </TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12 hidden md:table-cell">
                    Role
                  </TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12 hidden lg:table-cell">
                    Contact
                  </TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12">
                    Status
                  </TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12 hidden sm:table-cell">
                    Date
                  </TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-600 h-12 text-right pr-6">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((app) => (
                  <TableRow
                    key={app.id}
                    className="border-zinc-900/50 hover:bg-zinc-900/30 transition-colors duration-200 cursor-pointer"
                    onClick={() => setSelectedApp(app)}
                    data-testid={`application-row-${app.id}`}
                  >
                    <TableCell className="pl-6 py-4">
                      <p className="text-white text-sm font-medium">{app.name}</p>
                    </TableCell>
                    <TableCell className="hidden md:table-cell py-4">
                      <span className="text-zinc-400 text-xs font-mono">
                        {app.role_title || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell py-4">
                      <p className="text-zinc-400 text-sm">{app.email}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">{app.mobile}</p>
                      {app.github_url && (
                        <a
                          href={app.github_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-[#BEF264] text-xs mt-1 inline-block hover:underline"
                        >
                          GitHub Profile
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge
                        data-testid={`status-badge-${app.id}`}
                        className={`rounded-full text-[10px] font-mono uppercase tracking-wider px-3 py-1 border ${
                          statusConfig[app.status]?.color || statusConfig.pending.color
                        }`}
                      >
                        {statusConfig[app.status]?.label || "Pending"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell py-4">
                      <span className="text-zinc-600 text-xs font-mono">
                        {formatDate(app.created_at)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right pr-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="w-8 h-8 flex items-center justify-center hover:bg-zinc-800 transition-colors duration-200"
                            data-testid={`actions-btn-${app.id}`}
                          >
                            <MoreVertical className="w-4 h-4 text-zinc-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-48 bg-[#0A0A0A] border-zinc-800 rounded-none"
                        >
                          <DropdownMenuLabel className="text-zinc-600 font-mono text-[10px] uppercase tracking-wider">
                            Update Status
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator className="bg-zinc-800" />
                          {["pending", "reviewed", "shortlisted", "rejected"].map((s) => (
                            <DropdownMenuItem
                              key={s}
                              data-testid={`set-status-${s}-${app.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                updateStatus(app.id, s);
                              }}
                              className="text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-wider cursor-pointer focus:bg-zinc-800 focus:text-white"
                            >
                              <span
                                className={`w-2 h-2 rounded-full mr-2 ${
                                  s === "pending"
                                    ? "bg-amber-400"
                                    : s === "reviewed"
                                    ? "bg-blue-400"
                                    : s === "shortlisted"
                                    ? "bg-[#BEF264]"
                                    : "bg-red-400"
                                }`}
                              />
                              {s}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="bg-zinc-800" />
                          <DropdownMenuItem
                            data-testid={`download-resume-${app.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadResume(app.id, app.resume_filename);
                            }}
                            className="text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-wider cursor-pointer focus:bg-zinc-800 focus:text-white"
                          >
                            <Download className="w-3.5 h-3.5 mr-2" />
                            Download Resume
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </motion.div>

        {/* Results count */}
        {!loading && (
          <p className="text-zinc-700 font-mono text-[10px] uppercase tracking-[0.15em] mt-4">
            Showing {filtered.length} of {apps.length} applications
          </p>
        )}
      </div>

      {/* Role Management Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-bold tracking-tight">
              {editingRole ? "Edit Role" : "Add New Role"}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-sm">
              {editingRole ? "Update the job role details" : "Create a new job role for applications"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div>
              <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-3 block">
                Role Title
              </Label>
              <Input
                value={roleForm.title}
                onChange={(e) => setRoleForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Software Engineer Intern"
                className="h-12 bg-black/40 border border-zinc-800 rounded-none focus:border-zinc-700 focus:ring-0 text-white placeholder:text-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-3 block">
                Short Description
              </Label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description shown on careers page"
                className="h-12 bg-black/40 border border-zinc-800 rounded-none focus:border-zinc-700 focus:ring-0 text-white placeholder:text-zinc-700"
              />
            </div>
            <div>
              <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-3 block">
                Details (HTML supported)
              </Label>
              <Textarea
                value={roleForm.details}
                onChange={(e) => setRoleForm((f) => ({ ...f, details: e.target.value }))}
                placeholder="<strong>Why Join BlankSage</strong><ul><li>Item 1</li></ul>"
                rows={6}
                className="bg-transparent border border-zinc-800 rounded-none focus:border-zinc-700 focus:ring-0 text-white placeholder:text-zinc-700 resize-none"
              />
            </div>
            <div>
              <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-3 block">
                Status
              </Label>
              <select
                value={roleForm.status}
                onChange={(e) => setRoleForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full h-12 px-4 bg-black/40 border border-zinc-800 text-zinc-400 font-mono text-sm focus:border-zinc-700 focus:ring-0 transition-colors duration-300"
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleRoleSubmit}
                className="flex-1 h-12 bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300"
              >
                {editingRole ? "Update Role" : "Create Role"}
              </Button>
              <Button
                onClick={() => setRoleDialogOpen(false)}
                className="h-12 px-6 bg-transparent border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedApp} onOpenChange={() => setSelectedApp(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none max-w-4xl max-h-[90vh] overflow-hidden" data-testid="application-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-bold tracking-tight">
              {selectedApp?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-sm">
              Application details
            </DialogDescription>
          </DialogHeader>
          {selectedApp && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-4 max-h-[calc(90vh-120px)] overflow-y-auto">
              {/* Left - Info */}
              <div className="lg:col-span-2 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">Role</p>
                    <p className="text-zinc-300 text-sm">{selectedApp.role_title || "—"}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">Email</p>
                    <p className="text-zinc-300 text-sm break-all">{selectedApp.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">Mobile</p>
                    <p className="text-zinc-300 text-sm">{selectedApp.mobile}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">Status</p>
                    <Badge
                      className={`rounded-full text-[10px] font-mono uppercase tracking-wider px-3 py-1 border ${
                        statusConfig[selectedApp.status]?.color || statusConfig.pending.color
                      }`}
                    >
                      {statusConfig[selectedApp.status]?.label || "Pending"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">Applied</p>
                    <p className="text-zinc-300 text-sm">{formatDate(selectedApp.created_at)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-1">GitHub URL</p>
                  {selectedApp.github_url ? (
                    <a
                      href={selectedApp.github_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#BEF264] text-sm break-all hover:underline"
                    >
                      {selectedApp.github_url}
                    </a>
                  ) : (
                    <p className="text-zinc-500 text-sm">Not provided</p>
                  )}
                </div>

                <div>
                  <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-2">
                    Interest
                  </p>
                  <div className="border border-zinc-800 p-4 bg-black/40">
                    <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                      {selectedApp.tech_interest || selectedApp.form_data?.marketing_experience || "—"}
                    </p>
                  </div>
                </div>

                {/* Form Data / Questionnaire Answers */}
                {selectedApp.form_data && Object.keys(selectedApp.form_data).length > 0 && (
                  <div>
                    <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] mb-3">
                      Questionnaire Answers
                    </p>
                    <div className="border border-zinc-800 bg-black/40 space-y-4 p-4 max-h-[300px] overflow-y-auto">
                      {Object.entries(selectedApp.form_data).map(([key, value]) => (
                        <div key={key}>
                          <p className="text-zinc-500 text-[10px] font-mono uppercase tracking-[0.1em] mb-1">
                            {key.replace(/_/g, " ")}
                          </p>
                          <p className="text-zinc-300 text-xs leading-relaxed whitespace-pre-wrap break-words mb-3">
                            {value || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    data-testid="dialog-download-resume"
                    onClick={() => downloadResume(selectedApp.id, selectedApp.resume_filename)}
                    className="flex-1 h-10 bg-transparent border border-zinc-800 text-zinc-400 hover:bg-zinc-800 hover:text-white font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300"
                  >
                    <Download className="w-3.5 h-3.5 mr-2" />
                    Download
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="dialog-update-status-btn"
                        className="flex-1 h-10 bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300"
                      >
                        Update Status
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-44 bg-[#0A0A0A] border-zinc-800 rounded-none"
                    >
                      {["pending", "reviewed", "shortlisted", "rejected"].map((s) => (
                        <DropdownMenuItem
                          key={s}
                          data-testid={`dialog-set-status-${s}`}
                          onClick={() => {
                            updateStatus(selectedApp.id, s);
                            setSelectedApp({ ...selectedApp, status: s });
                          }}
                          className="text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-wider cursor-pointer focus:bg-zinc-800 focus:text-white"
                        >
                          <span
                            className={`w-2 h-2 rounded-full mr-2 ${
                              s === "pending"
                                ? "bg-amber-400"
                                : s === "reviewed"
                                ? "bg-blue-400"
                                : s === "shortlisted"
                                ? "bg-[#BEF264]"
                                : "bg-red-400"
                            }`}
                          />
                          {s}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Right - PDF Viewer */}
              <div className="lg:col-span-3 border border-zinc-800 bg-zinc-950 min-h-[500px]">
                <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-black/60">
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" />
                    {selectedApp.resume_filename}
                  </span>
                </div>
                <iframe
                  data-testid="resume-pdf-viewer"
                  src={`${API}/applications/${selectedApp.id}/resume`}
                  title="Resume PDF"
                  className="w-full bg-white"
                  style={{ height: "470px" }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}